import type { ReactNode } from 'react';

interface OnboardingShellProps {
  /** 1-based index of the step on screen. Pass `totalSteps + 1` for a success panel. */
  step: number;
  /** How many dots to draw. Three on the invitation flow, two behind the gate. */
  totalSteps: number;
  title: string;
  subtitle: string;
  /** Top-right escape hatch — "Sign out" once there is a session to leave. */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * The dark, full-screen frame around the partner registration wizard: gold-dot
 * background, brand bar, step dots, and the frosted card the steps render in.
 *
 * Lifted straight from the partner-register.html mock (see the `ed-*` classes in
 * index.css), and deliberately unlike the rest of the app: everything else is
 * the cream CMS, whereas this is the one screen a partner manager sees before they
 * are let in, so it reads as a threshold rather than as a page.
 *
 * Both hosts share it — the invitation wizard and `PartnerOnboardingGate` — which
 * is what makes an interrupted sign-up resume without looking like a different
 * product.
 */
export function OnboardingShell({ step, totalSteps, title, subtitle, action, children }: OnboardingShellProps) {
  return (
    <div className='tw:min-h-screen tw:bg-ed-ink tw:font-sans tw:text-ed-cream'>
      <div className='ed-bg-pattern' />
      <div className='ed-ink-wash' />

      <nav className='tw:fixed tw:z-50 tw:w-full tw:border-b tw:border-white/5 tw:bg-ed-black/90 tw:backdrop-blur-md'>
        <div className='tw:mx-auto tw:flex tw:h-20 tw:max-w-7xl tw:items-center tw:justify-between tw:px-6 tw:lg:px-8'>
          <div className='tw:flex tw:items-center tw:gap-3'>
            <img src='/eightydays_balloon.png' alt='' className='tw:h-9 tw:w-auto tw:object-contain' />
            <span className='tw:font-serif tw:text-xl tw:tracking-wider tw:text-white tw:uppercase'>Eighty Days</span>
          </div>
          {action}
        </div>
      </nav>

      <main className='tw:relative tw:z-10 tw:px-6 tw:pt-32 tw:pb-20'>
        <div className='tw:mx-auto tw:max-w-4xl'>
          <div className='tw:mb-12 tw:text-center'>
            <h1 className='tw:mb-4 tw:font-serif tw:text-4xl tw:text-white tw:md:text-5xl'>{title}</h1>
            <p className='tw:text-white/60'>{subtitle}</p>
          </div>

          {/* Dots fill in cumulatively, so the row doubles as a progress bar. */}
          <div className='tw:mb-12 tw:flex tw:justify-center tw:gap-4'>
            {Array.from({ length: totalSteps }, (_, i) => (
              <div key={i} className='tw:flex tw:items-center tw:gap-4'>
                {i > 0 && <span className='tw:h-px tw:w-16 tw:bg-white/20' />}
                <span
                  className={`ed-step-indicator tw:flex tw:size-10 tw:items-center tw:justify-center tw:rounded-full tw:text-sm tw:font-medium ${
                    i < step ? 'ed-step-indicator-active' : 'tw:bg-white/10'
                  }`}
                >
                  {i + 1}
                </span>
              </div>
            ))}
          </div>

          <div className='ed-register-card tw:rounded-sm tw:p-8 tw:md:p-12'>{children}</div>
        </div>
      </main>
    </div>
  );
}

/** Heading pair at the top of each step, inside the card. */
export function OnboardingStepHeader({ title, blurb }: { title: string; blurb: string }) {
  return (
    <>
      <h2 className='tw:mb-2 tw:font-serif tw:text-2xl tw:text-white'>{title}</h2>
      <p className='tw:mb-8 tw:text-sm tw:text-white/50'>{blurb}</p>
    </>
  );
}
